import { Color, colors, gradients, MOTION, RADIUS } from "@elyxeer/ui";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import Box from "@mui/material/Box";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";

import { INTL_LOCALE, useLocale } from "~/i18n";
import { takenAt } from "~/lib/routine";
import type { RoutineCheckIn, RoutineSlot } from "~/types/routine";

interface SlotLook {
  icon: ComponentType<SvgIconProps>;
  /** Palette key for the icon on an empty tile. */
  tint: "morning.main" | "evening.main";
  /** The brand's own ritual gradient, painted once the dose is in. */
  fill: string;
  /**
   * Ink on that fill. Fixed rather than semantic because the gradients are the same in
   * both schemes: Stem Black on the morning's violet end is 4.93:1, Silver100 on the
   * evening's violet end 7.41:1 — each measured at the lighter-contrast stop.
   */
  ink: string;
}

const SLOT_LOOK: Record<RoutineSlot, SlotLook> = {
  morning: {
    icon: WbSunnyOutlinedIcon,
    tint: "morning.main",
    fill: gradients.morning,
    ink: colors[Color.Black900],
  },
  evening: {
    icon: BedtimeOutlinedIcon,
    tint: "evening.main",
    fill: gradients.evening,
    ink: colors[Color.Silver100],
  },
};

/**
 * One dose of one day — the thing the member taps with a sachet in the other hand.
 *
 * A single large target rather than a checkbox beside a label: the whole tile is the
 * control, it flips to the slot's ritual gradient the moment it is pressed, and it says
 * when the dose was taken. A day that can no longer be changed renders the same tile as
 * a plain block, so the history reads exactly like the present.
 */
export function DoseTile({
  slot,
  checkIn,
  isPast,
  loading = false,
  pending = false,
  readOnly = false,
  onToggle,
}: {
  slot: RoutineSlot;
  checkIn: RoutineCheckIn | undefined;
  /** An earlier day: an empty tile then means "missed", not "not yet". */
  isPast: boolean;
  /** The day's doses are still being read; the tile claims nothing yet. */
  loading?: boolean;
  /** A tap on this tile is on its way to the server. */
  pending?: boolean;
  readOnly?: boolean;
  onToggle?: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const look = SLOT_LOOK[slot];
  const Icon = look.icon;
  const taken = checkIn !== undefined;

  const time = checkIn ? takenAt(checkIn) : null;
  const status = loading
    ? ""
    : taken
      ? time
        ? t("routine.takenAt", {
            time: new Intl.DateTimeFormat(INTL_LOCALE[locale], {
              hour: "numeric",
              minute: "2-digit",
            }).format(time),
          })
        : t("routine.taken")
      : isPast
        ? t("routine.missed")
        : t("routine.notYet");

  const interactive = !readOnly && !loading;
  const control = interactive
    ? {
        component: "button" as const,
        type: "button" as const,
        onClick: onToggle,
        disabled: pending,
        "aria-pressed": taken,
      }
    : { component: "div" as const };

  return (
    <Box
      {...control}
      aria-label={`${t(`routine.${slot}Long`)}: ${status}`}
      data-testid={`routine.dose-${slot}`}
      data-taken={taken}
      sx={{
        flex: 1,
        minWidth: 0,
        // Stacked rather than one row: half a phone's width cannot hold an icon, a
        // check and "Užito v 21:15" side by side without wrapping the time away from
        // its label. The icons take the top line; the words get the full width.
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1,
        padding: { xs: 1.5, md: 2 },
        borderRadius: `${RADIUS.md}px`,
        border: "1px solid",
        borderColor: taken ? "transparent" : "divider",
        background: taken ? look.fill : "transparent",
        color: taken ? look.ink : "text.primary",
        font: "inherit",
        textAlign: "start",
        cursor: interactive ? "pointer" : "default",
        transition: `background-color ${MOTION.transition}, border-color ${MOTION.transition}`,
        ...(interactive && !taken
          ? { "&:hover": { backgroundColor: "surface.inset" } }
          : {}),
        "&:disabled": { cursor: "progress" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Icon
          aria-hidden="true"
          sx={{ fontSize: 22, color: taken ? "inherit" : look.tint }}
        />
        {taken ? (
          <CheckCircleIcon aria-hidden="true" sx={{ fontSize: 22 }} />
        ) : (
          <RadioButtonUncheckedIcon
            aria-hidden="true"
            sx={{
              fontSize: 22,
              color: "text.muted",
              // Nothing to press on a day that is closed.
              visibility: interactive ? "visible" : "hidden",
            }}
          />
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography variant="L1" sx={{ color: "inherit" }}>
          {t(`routine.${slot}`)}
        </Typography>
        <Typography
          variant="T3"
          sx={{
            color: taken ? "inherit" : "text.muted",
            // Held open while loading, so the tile does not jump when the status lands.
            minHeight: "1.5em",
          }}
        >
          {status}
        </Typography>
      </Box>
    </Box>
  );
}
